// Почта и код из письма: правила шага — одни на экран входа и на окно перед
// оплатой. SMY-175, SMY-341
//
// Регистрация в кабинете — одна реализация, и это записано давно (шапка
// `AskSignup` в screens/Onboarding.tsx): вторая разошлась бы с первой на первой
// же правке. С 21.09.2026 почту и код спрашивает ещё и окно перед оплатой
// гостя (apps/web/src/purchase.tsx), и то, что решает исход шага, — пауза
// перед повтором, неверный код, нет связи, — живёт здесь, а не в разметке. У
// экрана и окна своя раскладка, правила общие.

import { useEffect, useState } from 'react'
import { secondsUntil } from '@smyslika/core'
import { ApiError, OfflineError, type Auth, type Reference } from '@smyslika/api-client'

/** Код подошёл: исход, `userId` и, у входа в существующий аккаунт, записка. */
export type EmailVerified = Awaited<ReturnType<Auth['verifyEmail']>>

/**
 * Пока сервер не ответил справочником — значения политики по умолчанию.
 *
 * Совпадают с POLICY на сервере и обязаны за ней следовать: рассинхрон здесь
 * даст поле, ждущее не столько цифр, сколько присылает сервер, и кнопку
 * повтора, загорающуюся раньше, чем сервер её примет.
 */
const FALLBACK_CODE_LENGTH = 6
const FALLBACK_RESEND = 60
const FALLBACK_DELIVERY = 60

/** Числа шага из справочника сервера. */
export function emailCodePolicy(reference: Reference | null): { codeLength: number; cooldown: number; delivery: number } {
  return {
    codeLength: reference?.auth?.email?.codeLength ?? FALLBACK_CODE_LENGTH,
    cooldown: reference?.auth?.email?.resendCooldownSeconds ?? FALLBACK_RESEND,
    delivery: reference?.auth?.email?.deliverySeconds ?? FALLBACK_DELIVERY,
  }
}

/** Текст неудачи для человека: нет связи — словами, остальное — текстом сервера. */
export function failText(e: unknown): string {
  if (e instanceof OfflineError) return 'Нет связи. Проверь интернет и попробуй снова.'
  return e instanceof Error ? e.message : 'Не получилось. Попробуй ещё раз.'
}

export type CodeRequested =
  | { ok: true; waitUntil: number }
  | { ok: false; error: string; waitUntil?: number }

/**
 * Попросить код на адрес.
 *
 * `waitUntil` — момент, когда откроется повтор. У отказа он есть, только когда
 * код УЖЕ отправлен (`too_soon`): оставить человека на шаге с адресом в этот
 * момент — тупик, код у него в почте, а ввести его некуда. Такой отказ пускает
 * дальше, к полю кода, и срок берёт из заголовка сервера — своё число было бы
 * пессимистичным.
 */
export async function requestCode(
  auth: Pick<Auth, 'requestEmailCode'>,
  email: string,
  cooldown: number,
  now: () => number = Date.now,
): Promise<CodeRequested> {
  try {
    const res = await auth.requestEmailCode(email)
    return { ok: true, waitUntil: now() + res.resendAfterSeconds * 1000 }
  } catch (e) {
    if (e instanceof ApiError && e.reason === 'too_soon') {
      return { ok: false, error: failText(e), waitUntil: now() + (e.retryAfterSeconds ?? cooldown) * 1000 }
    }
    return { ok: false, error: failText(e) }
  }
}

export type CodeVerified =
  | { ok: true; verified: EmailVerified }
  | { ok: false; error: string; clearCode: boolean }

/**
 * Проверить код. Токен нового входа клиент кладёт сам (`verifyEmail` в
 * api-client); у гостя, подтвердившего свободную почту, токен прежний — его
 * перекладывает сессия (`keepSignedIn`, apps/web/src/token-store.ts).
 *
 * `clearCode` — очистить поле: следующее действие человека — ввод заново, и
 * заставлять его сначала стирать неверное значит добавлять работу к неудаче. На
 * 429 не чистим: пробовать сейчас всё равно нечего.
 */
export async function verifyCode(auth: Pick<Auth, 'verifyEmail'>, email: string, code: string): Promise<CodeVerified> {
  try {
    return { ok: true, verified: await auth.verifyEmail(email, code, 'Веб') }
  } catch (e) {
    return { ok: false, error: failText(e), clearCode: e instanceof ApiError && e.status !== 429 }
  }
}

/**
 * Обратный отсчёт до нового кода — по СРОКУ, а не по числу тиков.
 *
 * Хранится момент, когда пауза кончится, а не оставшиеся секунды. Разница
 * видна ровно тогда, когда вкладка уходит из виду: браузер придерживает
 * таймеры фоновой вкладки, и отсчёт, уменьшавший своё число раз в
 * срабатывание, отставал от часов на всё время отсутствия. Человек возвращался
 * через семь минут и видел на кнопке четыре — ждать ему предлагалось второй
 * раз то, что сервер уже отсчитал.
 *
 * `visibilitychange` здесь не для точности, а для мгновенности: срок и так
 * пересчитается на ближайшем тике, но между возвращением и тиком человек
 * успевает прочитать вчерашнее число.
 */
export function useCountdown(deadline: number | null): number {
  const [left, setLeft] = useState(() => (deadline == null ? 0 : secondsUntil(deadline)))

  useEffect(() => {
    if (deadline == null) {
      setLeft(0)
      return
    }
    // Тик останавливается на нуле сам: дальше ему нечего пересчитывать, а
    // секундный таймер, доживающий до конца экрана, — это работа впустую.
    let timer: ReturnType<typeof setInterval> | undefined
    const tick = (): void => {
      const left = secondsUntil(deadline)
      setLeft(left)
      if (left === 0 && timer != null) clearInterval(timer)
    }
    tick()
    timer = setInterval(tick, 1000)
    document.addEventListener('visibilitychange', tick)
    return () => {
      clearInterval(timer)
      document.removeEventListener('visibilitychange', tick)
    }
  }, [deadline])

  return left
}
